import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Activity, CalendarDays, Timer } from "lucide-react";

function formatHours(mins) {
  if (!mins) return "0h";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins - h * 60);
  return `${h}h ${m}m`;
}

const StatCard = ({ label, value, icon: Icon, testId }) => (
  <div className="bg-white border border-brand-border rounded-md p-6" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="overline">{label}</p>
      <Icon className="w-4 h-4 text-brand-muted" />
    </div>
    <p className="font-heading text-4xl font-light tracking-tight mt-4">{value}</p>
  </div>
);

export default function Overview() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/records/summary");
        setSummary(data);
      } catch {
        setSummary({ volunteers: [], events: [], active_now: 0, total_events: 0, total_records: 0 });
      }
    })();
  }, []);

  const totalMinutes = (summary?.volunteers || []).reduce((a, v) => a + (v.total_minutes || 0), 0);

  return (
    <div className="max-w-6xl" data-testid="overview-page">
      <p className="overline">Dashboard</p>
      <h1 className="font-heading text-4xl font-light tracking-tight mt-2">Overview</h1>
      <p className="text-brand-muted font-body mt-2">A quick snapshot of your volunteer activity.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
        <StatCard label="Clocked in now" value={summary?.active_now ?? "—"} icon={Activity} testId="stat-active" />
        <StatCard label="Total hours" value={formatHours(totalMinutes)} icon={Timer} testId="stat-hours" />
        <StatCard label="Events" value={summary?.total_events ?? "—"} icon={CalendarDays} testId="stat-events" />
        <StatCard label="Sessions" value={summary?.total_records ?? "—"} icon={Users} testId="stat-sessions" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-white border border-brand-border rounded-md p-6" data-testid="top-volunteers-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl">Top volunteers</h2>
            <span className="overline">by hours</span>
          </div>
          {summary && summary.volunteers.length === 0 && (
            <p className="text-brand-muted text-sm">No completed sessions yet.</p>
          )}
          <ul className="divide-y divide-brand-border">
            {(summary?.volunteers || []).slice(0, 8).map((v) => (
              <li key={v.volunteer_name} className="py-3 flex items-center justify-between">
                <div>
                  <p className="font-body font-medium">{v.volunteer_name}</p>
                  <p className="text-xs text-brand-muted">{v.sessions} session{v.sessions !== 1 ? "s" : ""}</p>
                </div>
                <span className="font-heading text-lg tabular-nums">{formatHours(v.total_minutes)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-brand-border rounded-md p-6" data-testid="top-events-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl">Hours by event</h2>
            <span className="overline">totals</span>
          </div>
          {summary && summary.events.length === 0 && (
            <p className="text-brand-muted text-sm">No completed sessions yet.</p>
          )}
          <ul className="divide-y divide-brand-border">
            {(summary?.events || []).slice(0, 8).map((e) => (
              <li key={e.event_id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="font-body font-medium">{e.event_name || "Unknown event"}</p>
                  <p className="text-xs text-brand-muted">{e.sessions} session{e.sessions !== 1 ? "s" : ""}</p>
                </div>
                <span className="font-heading text-lg tabular-nums">{formatHours(e.total_minutes)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
